import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { MessageCircle, Menu, X, Phone } from "lucide-react";
import { WHATSAPP_URL } from "../lib/whatsapp";
import { PHONE_TEL } from "../lib/contact";

const navLinkClass = ({ isActive }) =>
  `text-xs lg:text-sm tracking-wide whitespace-nowrap transition-colors duration-200 ${
    isActive ? "text-[#e6c87a]" : "text-[#a09a8c] hover:text-[#f2ede3]"
  }`;

const mobileLinkClass = ({ isActive }) =>
  `block border-b border-[rgba(201,162,75,0.12)] px-5 py-4 text-sm tracking-wide transition-colors duration-200 ${
    isActive ? "text-[#e6c87a]" : "text-[#a09a8c] hover:text-[#f2ede3]"
  }`;

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-[rgba(201,162,75,0.22)] bg-[#0a0a09]/90 backdrop-blur-md lg:hidden"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-2.5 sm:gap-3"
          onClick={closeMenu}
        >
          <img
            src="/edt-crest.png"
            alt="EDT Claims Solution & Services crest"
            data-testid="header-logo-image"
            className="h-11 w-auto object-contain sm:h-14"
          />
          <span className="font-display leading-tight text-[#f2ede3]">
            <span className="block text-sm font-semibold tracking-wide sm:text-lg">
              EDT Claims
            </span>
            <span className="block text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#c9a24b] sm:text-[0.72rem]">
              Solution &amp; Services
            </span>
          </span>
        </Link>

        <nav
          data-testid="main-nav"
          className="ml-4 hidden items-center gap-6 lg:flex"
        >
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home-link">
            Home
          </NavLink>
          <NavLink
            to="/driving-tests"
            className={navLinkClass}
            data-testid="nav-driving-tests-link"
          >
            Driving Tests
          </NavLink>
          <NavLink to="/claims" className={navLinkClass} data-testid="nav-claims-link">
            Motor Claims Assist
          </NavLink>
          <NavLink to="/reviews" className={navLinkClass} data-testid="nav-reviews-link">
            Reviews &amp; Testimonials
          </NavLink>
          <NavLink to="/faq-contact" className={navLinkClass} data-testid="nav-faq-contact-link">
            FAQ &amp; Contact
          </NavLink>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="inline-flex items-center gap-2 border border-[rgba(201,162,75,0.5)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
        </nav>

        <button
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          data-testid="mobile-menu-button"
          onClick={() => setMenuOpen((open) => !open)}
          className="flex h-10 w-10 items-center justify-center border border-[rgba(201,162,75,0.4)] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09] lg:hidden"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {menuOpen && (
        <nav
          data-testid="mobile-menu"
          className="border-t border-[rgba(201,162,75,0.22)] bg-[#0a0a09] lg:hidden"
        >
          <NavLink to="/" end className={mobileLinkClass} data-testid="mobile-nav-home-link" onClick={closeMenu}>
            Home
          </NavLink>
          <NavLink to="/driving-tests" className={mobileLinkClass} data-testid="mobile-nav-driving-tests-link" onClick={closeMenu}>
            Driving Tests
          </NavLink>
          <NavLink to="/claims" className={mobileLinkClass} data-testid="mobile-nav-claims-link" onClick={closeMenu}>
            Motor Claims Assist
          </NavLink>
          <NavLink to="/reviews" className={mobileLinkClass} data-testid="mobile-nav-reviews-link" onClick={closeMenu}>
            Reviews &amp; Testimonials
          </NavLink>
          <NavLink to="/faq-contact" className={mobileLinkClass} data-testid="mobile-nav-faq-contact-link" onClick={closeMenu}>
            FAQ &amp; Contact
          </NavLink>
          <div className="flex items-center gap-3 px-5 py-4">
            <a
              href={PHONE_TEL}
              data-testid="mobile-nav-call-button"
              onClick={closeMenu}
              className="flex flex-1 items-center justify-center gap-2 border border-[rgba(201,162,75,0.5)] py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
            >
              <Phone size={15} />
              Call
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-whatsapp-button"
              onClick={closeMenu}
              className="flex flex-1 items-center justify-center gap-2 border border-[rgba(201,162,75,0.5)] py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
            >
              <MessageCircle size={15} />
              WhatsApp
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};
